import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Paper,
  Select,
  SelectChangeEvent,
  Typography,
} from '@mui/material'

import { DateCalendar, LocalizationProvider } from '@mui/x-date-pickers-pro'
import { AdapterDayjs } from '@mui/x-date-pickers-pro/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'

import { ETH_ADDR_ZERO, EthAddr, PoolAccountAccessPair } from '@shared/SchemaTypes'

import LenderPoolHoldings from '@react/lender/LenderPoolHoldings'

import React, { useEffect } from 'react'

import ButtonModal from '@react/components/ButtonModal'
import DynamicCreditDebitForm from '@react/dynamic/DynamicCreditDebitForm'
import { fetchPoolParticipants } from '@react/fetchers/PoolFetchers'
import { useOtChainState, useOtChainTimestamp, useOtSelector } from '@react/redux/store'
import { PoolTypeEnum, RoleEnum } from '@shared/Enums'
import { PoolDynamicOverviewCTO } from '@shared/PoolDynamicCTO'
import { logOT } from '@shared/loggingShared'
import { findBlockByTimestamp } from '@shared/sharedUtils'
import { CompanyProfileAddressItem } from './CompanyProfileAddressItem'
import { toCompanyAddrString } from '@react/fetchers/companyProfileFetchers'
import { AddressPill } from '@react/components/addresses/AddressPill'

const SierraMarketMakerDropdownItem: React.FC<{ walletAddr: EthAddr; hasBorder?: boolean }> = ({
  walletAddr,
  hasBorder,
}): React.ReactElement => {
  const companyProfiles = useOtSelector((state) => state.poolState.companyProfiles)
  const { companyProfile } = toCompanyAddrString(walletAddr, companyProfiles)

  const defaultName = companyProfile ? companyProfile.companyName : `Market Maker`

  return (
    <Box
      sx={{
        width: '100%',
        borderWidth: hasBorder ? 1 : 0,
        borderColor: 'grey.500',
        borderStyle: 'solid',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: 1,
        padding: 1,
      }}
    >
      <Typography variant="bodyBold">{defaultName}</Typography>
      <Typography variant="caption" sx={{ marginLeft: 1 }}>
        <AddressPill addr={walletAddr} />
      </Typography>
    </Box>
  )
}

export const AdminLenderView = (props: { poolAddr: EthAddr }): React.ReactElement => {
  const {
    stageConfiguration: { restAPIKey, serverApiBaseUrl },
    userConnection: { roleEnum },
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()

  const [participants, setParticipants] = React.useState<PoolAccountAccessPair[]>([])
  const [selectedParticipant, setSelectedParticipant] = React.useState<EthAddr>('')
  const [selectingDate, setSelectingDate] = React.useState<boolean>(false)

  const chainTimestamp = useOtChainTimestamp()

  const [blockNumberSnapshot, setBlockNumberSnapshot] = React.useState<number | undefined>(undefined)
  const [dateValue, setDateValue] = React.useState<Dayjs>(dayjs((chainTimestamp ?? 0) * 1000))
  const { poolAddr } = props
  const poolBaseOverviewCTO = useOtSelector((state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO])

  const poolDynamicOverviewCTO =
    poolBaseOverviewCTO?.poolType === PoolTypeEnum.Dynamic ? (poolBaseOverviewCTO as PoolDynamicOverviewCTO) : null

  useEffect(() => {
    if (chainTimestamp) {
      setDateValue(dayjs(chainTimestamp * 1000))
    }
  }, [chainTimestamp])

  useEffect(() => {
    if (chainTimestamp) {
      setDateValue(dayjs(chainTimestamp * 1000))
    }
  }, [chainTimestamp])

  useEffect(() => {
    if (providerReadOnly && poolAddr && serverApiBaseUrl && restAPIKey) {
      fetchPoolParticipants(serverApiBaseUrl, restAPIKey, poolAddr)
        .then((_participants) => {
          logOT.debug('all users', _participants)
          setParticipants(_participants)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }, [providerReadOnly, poolAddr])

  return (
    <Paper>
      <form>
        <Box px={3} py={2}>
          <Typography variant="bodyBold" align="center" margin="dense">
            View Lenders Account view for pool
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6} sm={6}>
              <FormControl sx={{ m: 1, width: '80%' }}>
                <InputLabel id="asset-label">Lender</InputLabel>
                <Select
                  labelId="asset-label"
                  id="asset-select"
                  input={<OutlinedInput id="asset-select" label="Asset" />}
                  value={selectedParticipant}
                  renderValue={(value: string) => {
                    return roleEnum === RoleEnum.ISSUER ? (
                      <SierraMarketMakerDropdownItem walletAddr={value} />
                    ) : (
                      <CompanyProfileAddressItem walletAddr={value} />
                    )
                  }}
                  onChange={(event: SelectChangeEvent) => {
                    logOT.info('event.target.value', event.target.value)
                    setSelectedParticipant(event.target.value as string)
                  }}
                >
                  {participants.map((p: PoolAccountAccessPair) => (
                    <MenuItem
                      value={p.accountAddr}
                      key={`asset-option-${p.accountAddr}`}
                      style={{
                        paddingLeft: 12,
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'flex-start',
                        alignItems: 'center',
                      }}
                    >
                      {roleEnum === RoleEnum.ISSUER ? (
                        <SierraMarketMakerDropdownItem hasBorder={true} walletAddr={p.accountAddr} />
                      ) : (
                        <CompanyProfileAddressItem hasBorder={true} walletAddr={p.accountAddr} />
                      )}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={3} sm={3}>
              {poolDynamicOverviewCTO && roleEnum === RoleEnum.POOL_ADMIN && selectedParticipant.length > 0 && (
                <ButtonModal title={'Debit or Credit'} modalWidth={900} variant="primary">
                  <DynamicCreditDebitForm poolAddr={poolAddr} lenderAddr={selectedParticipant} />
                </ButtonModal>
              )}
            </Grid>
            <Grid item xs={3} sm={3}>
              {!selectingDate && (
                <Typography variant="bodyBold" align="center" margin="dense" onClick={() => setSelectingDate(true)}>
                  Date {dateValue.format('YYYY-MM-DD')}
                </Typography>
              )}
              {selectingDate && providerReadOnly && (
                <>
                  <InputLabel id="asset-label">Date {dateValue.format('YYYY-MM-DD')} </InputLabel>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateCalendar
                      value={dateValue}
                      onChange={(newValue) => {
                        logOT.info('New Date value', newValue)
                        setSelectingDate(false)

                        setDateValue(newValue)
                        const blockTimeStamp = newValue.unix()
                        findBlockByTimestamp(providerReadOnly, blockTimeStamp)
                          .then((_blockNumber: number) => {
                            logOT.info('blockNumber', blockNumberSnapshot)
                            setBlockNumberSnapshot(_blockNumber)
                          })
                          .catch((error) => {
                            console.error('Error finding block', error)
                          })
                      }}
                    />
                  </LocalizationProvider>
                </>
              )}
            </Grid>
            <Grid item xs={12} sm={12}>
              {poolAddr && selectedParticipant !== '' && (
                <LenderPoolHoldings
                  poolAddr={poolAddr}
                  accountAddr={selectedParticipant}
                  blockNumberSnapshot={blockNumberSnapshot}
                />
              )}
            </Grid>
          </Grid>
        </Box>
      </form>
    </Paper>
  )
}
